import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '@/features/auth'
import type { EnrollmentData, EnrollmentStep } from '@/features/enrollment'
import { getInitialEnrollmentData } from '../utils/enrollmentDataUtils'
import { SessionManager } from '../utils/sessionManager'
import { NavigationManager } from '../utils/navigationManager'
import { LifecycleManager } from '../utils/lifecycleManager'
import { validateStepByCampaign } from '../utils/validation/stepValidation'
import { getCampaignConfig } from '../config/campaigns.config'

/**
 * Custom hook for managing enrollment wizard state and logic
 * FIXED VERSION - Resolves navigation inconsistencies
 */
export const useEnrollmentWizard = (onCampaignSelect?: (campaign: any) => void) => {
  const { user } = useAuth()

  // Get agent name from user context safely
  const agentName = (user?.firstName || user?.lastName)
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
    : 'Unknown Agent'

  // Initialize session ID from localStorage
  const [sessionId, setSessionId] = useState<string | null>(() =>
    SessionManager.initializeSessionId()
  )

  // Core state
  const [enrollmentData, setEnrollmentData] = useState<EnrollmentData>(
    getInitialEnrollmentData(agentName)
  )
  const [currentWizardStepIndex, setCurrentWizardStepIndex] = useState(0)
  const [isCompletedMode, setIsCompletedMode] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isNavigating, setIsNavigating] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const [isStartingNew, setIsStartingNew] = useState(false)

  // Refs for managing state and preventing race conditions
  const enrollmentDataRef = useRef<EnrollmentData>(enrollmentData)
  const skipResumeOnceRef = useRef<boolean>(false)
  const movedToPendingRef = useRef<boolean>(false)
  const hiddenTimerRef = useRef<number | null>(null)
  const isRestoringSessionRef = useRef<boolean>(false)

  useEffect(() => {
    // ✅ Wait for user to be loaded
    if (!user?.id) {
      console.log('⏳ [useEnrollmentWizard] Waiting for user information...');
      return
    }

    const currentAgentId = enrollmentDataRef.current.agentId
    const agentCode = (user as any).agentCode
    const userFirstName = user.firstName || ''
    const userLastName = user.lastName || ''

    const namesMissing = !enrollmentDataRef.current.referralAgentFirstName ||
      !enrollmentDataRef.current.referralAgentLastName ||
      enrollmentDataRef.current.referralAgentFirstName === 'undefined' ||
      enrollmentDataRef.current.referralAgentLastName === 'undefined'

    // ✅ Check if we need to set agentId or names
    const needsUpdate =
      !currentAgentId ||
      currentAgentId === '' ||
      (agentCode && currentAgentId !== agentCode) ||
      namesMissing

    console.log('🕵️ [useEnrollmentWizard] User update check:', { needsUpdate, userFirstName, userLastName });

    if (needsUpdate) {
      // Create a clean agent name
      const freshAgentName = [userFirstName, userLastName].filter(Boolean).join(' ') || 'Unknown Agent'

      setEnrollmentData(prev => {
        // Only update if data is actually different
        if (prev.referralAgentFirstName === userFirstName &&
          prev.referralAgentLastName === userLastName &&
          prev.agentId === (agentCode || user.id)) {
          return prev;
        }

        console.log('✅ [useEnrollmentWizard] Updating agent info:', { userFirstName, userLastName });

        const next = {
          ...prev,
          agentId: agentCode || user.id,
          agentName: freshAgentName !== 'Unknown Agent' ? freshAgentName : (prev.agentName || 'Unknown Agent'),
          referralAgentFirstName: userFirstName || prev.referralAgentFirstName || '',
          referralAgentLastName: userLastName || prev.referralAgentLastName || '',
        } as EnrollmentData

        enrollmentDataRef.current = next;
        return next;
      })
    }
  }, [
    user?.id,
    user?.firstName,
    user?.lastName,
    (user as any)?.agentCode,
    enrollmentData.agentId,
    enrollmentData.referralAgentFirstName,
    enrollmentData.referralAgentLastName
  ])

  // Initialize managers
  const sessionManager = new SessionManager(
    sessionId,
    setSessionId,
    enrollmentDataRef,
    skipResumeOnceRef,
    movedToPendingRef,
    isRestoringSessionRef
  )

  const navigationManager = new NavigationManager(
    sessionManager,
    currentWizardStepIndex,
    setCurrentWizardStepIndex,
    isCompletedMode
  )

  const lifecycleManager = new LifecycleManager(
    sessionManager,
    enrollmentDataRef,
    agentName
  )

  const applyMetaFromSession = useCallback((session: any) => {
    if (!session?.data) {
      return
    }

    const savedData = session.data as Record<string, any>
    const metaFields = ['listId', 'organizationId'] as const

    const updates: Partial<EnrollmentData> = {}
    for (const field of metaFields) {
      if (Object.prototype.hasOwnProperty.call(savedData, field)) {
        const value = savedData[field]
        if (value !== undefined && (enrollmentDataRef.current as any)[field] !== value) {
          updates[field] = value
        }
      }
    }

    if (Object.keys(updates).length === 0) {
      return
    }

    setEnrollmentData(prev => {
      const next = { ...prev, ...updates } as EnrollmentData
      enrollmentDataRef.current = next
      return next
    })
  }, [setEnrollmentData, enrollmentDataRef])

  // Reset enrollment function
  const resetEnrollment = useCallback(() => {
    lifecycleManager.resetEnrollment(
      setEnrollmentData,
      setCurrentWizardStepIndex,
      setIsCompletedMode
    )
  }, [lifecycleManager])

  /**
   * FIXED: Main navigation handler with proper state management
   */
  const handleNavigate = useCallback(async (targetIndex: number, steps?: EnrollmentStep[]) => {


    if (!steps || steps.length === 0) {
      console.warn('⚠️ No steps provided to handleNavigate')
      return
    }

    // Prevent navigation if already navigating
    if (isNavigating) {
      console.warn('⚠️ Already navigating, skipping request')
      return
    }

    setIsNavigating(true)
    try {
      const saved = await navigationManager.handleNavigate(targetIndex, enrollmentDataRef, steps)
      applyMetaFromSession(saved)
    } catch (error) {
      console.error('❌ Navigation error:', error)
    } finally {
      setIsNavigating(false)
    }
  }, [currentWizardStepIndex, isNavigating, navigationManager, applyMetaFromSession])

  /**
   * FIXED: Simple navigation without saving - for back buttons
   * This is synchronous and immediate
   */
  const handleSimpleNavigate = useCallback((targetIndex: number) => {


    // Validate target index
    if (targetIndex < 0) {
      console.error('❌ Invalid target index (negative):', targetIndex)
      return
    }

    // Don't navigate if already on target step
    if (targetIndex === currentWizardStepIndex) {
      return
    }

    // Update step index immediately
    setCurrentWizardStepIndex(targetIndex)

    // Save to localStorage
    try {
      localStorage.setItem('enrollmentCurrentStep', targetIndex.toString())
    } catch (error) {
      console.error('❌ Failed to save step to localStorage:', error)
    }

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })

  }, [currentWizardStepIndex])

  /**
 * ✅ UPDATED: Campaign-aware step validation with optional step handling
 */
  const validateStepCompletionInternal = useCallback((
    stepIndex: number,
    stepData: any,
    campaignId?: string
  ) => {
    if (!stepData) return { isCompleted: false, isSkipped: false, isEmpty: true }

    const activeCampaignId = campaignId || enrollmentDataRef.current.campaignId || enrollmentDataRef.current.selectedCampaign

    if (!activeCampaignId) {
      console.warn('⚠️ No campaign ID found for step validation')

      // Navigate back to campaign selector if callback is provided
      if (onCampaignSelect) {
        onCampaignSelect(null)
      }
      return { isCompleted: false, isSkipped: false, isEmpty: true }
    }


    // Use campaign-aware validation
    const validation = validateStepByCampaign(activeCampaignId, stepIndex, stepData)


    return validation
  }, [])

  /**
   * Legacy boolean validation for backward compatibility
   */
  const validateStepCompletion = useCallback((stepIndex: number, stepData: any, campaignId?: string): boolean => {
    const result = validateStepCompletionInternal(stepIndex, stepData, campaignId)
    return result.isCompleted
  }, [validateStepCompletionInternal])

  /**
   * OLD VALIDATION CODE - KEEPING FOR REFERENCE BUT NOT USED
   */
  const validateStepCompletionOld = useCallback((stepIndex: number, stepData: any, campaignId?: string) => {
    if (!stepData) return false


    // First, check if the step already has isCompleted flag set to true
    // This respects the existing completion status
    if (stepData.isCompleted === true) {
      return true
    }

    // If isSkipped is explicitly true, don't mark as completed
    if (stepData.isSkipped === true) {
      return false
    }

    // Step 0: Intake Contact - Check if essential contact info is provided
    if (stepIndex === 0) {
      const hasContactInfo = stepData.clientEmail || stepData.intakeEmail || stepData.intakePhone || stepData.email
      const hasRelationInfo = stepData.relationToVictim || stepData.relation


      if (stepData.gamerTagsSkipped === true) {
        return hasContactInfo && hasRelationInfo
      }

      const hasPlatformData = stepData.selectedPlatforms && stepData.selectedPlatforms.length > 0
      return hasContactInfo && hasRelationInfo && hasPlatformData
    }

    // Step 1: Injured Party - Check if essential info is provided
    if (stepIndex === 1) {
      const hasEssentialInfo = stepData.injuredPartyFirstName &&
        stepData.injuredPartyLastName &&
        stepData.clientEmail &&
        stepData.birthDay &&
        stepData.birthMonth &&
        stepData.birthYear

      return hasEssentialInfo
    }

    // Step 2: Exploitation Details - Check if at least some gaming info is provided
    if (stepIndex === 2) {
      const hasGamingInfo = stepData.ageStartedGaming ||
        stepData.hoursPerDay ||
        (stepData.gamingDisorderSymptoms && stepData.gamingDisorderSymptoms.length > 0)

      return hasGamingInfo
    }

    // Step 3: Gaming Details - Check if gaming info is provided
    if (stepIndex === 3) {
      const hasGamingInfo = stepData.isCurrentlyInSchool !== undefined ||
        stepData.firstVideoGame ||
        stepData.monthlySubscriptions && stepData.monthlySubscriptions.length > 0 ||
        stepData.averageHoursPerDay ||
        stepData.monthlySpending

      return hasGamingInfo
    }

    // Step 4: Health & Legal - Check if health/legal info is provided
    if (stepIndex === 4) {
      const hasHealthLegalInfo = stepData.medicalTreatments && stepData.medicalTreatments.length > 0

      return hasHealthLegalInfo
    }

    // ⭐ CAMPAIGN-AWARE VALIDATION
    const getCampaignConfig = require('../config/campaigns.config').getCampaignConfig
    const campaign = getCampaignConfig(campaignId || enrollmentDataRef.current.campaignId)

    if (!campaign) {
      console.error('❌ Campaign not found during validation:', campaignId || enrollmentDataRef.current.campaignId)

      // Navigate back to campaign selector if callback is provided
      if (onCampaignSelect) {
        onCampaignSelect(null)
      }
      return false
    }

    if (campaign) {
      const currentStep = campaign.steps[stepIndex]
      const stepId = currentStep?.id
      const componentName = currentStep?.component
      const autoCompleteIfEmpty = currentStep?.autoCompleteIfEmpty



      // Check if step should auto-complete even if empty
      if (autoCompleteIfEmpty) {
        return true
      }

      // Identity Preview, Submit, Signature, FollowUp are always completed
      if (['IdentityPreview', 'Submit', 'Signature', 'FollowUp'].includes(componentName)) {
        return true
      }
    }

    // Default: If we have any meaningful data, consider it completed
    const hasAnyData = Object.keys(stepData).length > 0
    return hasAnyData
  }, [])
  /**
   * Mark step as skipped
   */
  const markStepSkipped = useCallback((stepIndex: number) => {

    navigationManager.markStepSkipped(stepIndex, enrollmentDataRef)

    // Also update the state to trigger re-render
    setEnrollmentData(prev => {
      const skippedSteps = prev.skippedSteps || []
      const completedSteps = prev.completedSteps || []

      return {
        ...prev,
        skippedSteps: [...skippedSteps.filter(s => s !== stepIndex), stepIndex],
        completedSteps: completedSteps.filter(s => s !== stepIndex)
      }
    })
  }, [navigationManager])

  /**
   * FIXED: Mark step as completed
   */
  const markStepCompleted = useCallback((stepIndex: number) => {

    navigationManager.markStepCompleted(stepIndex, enrollmentDataRef)

    // Also update the state to trigger re-render
    setEnrollmentData(prev => {
      const completedSteps = prev.completedSteps || []

      // Don't add if already completed
      if (completedSteps.includes(stepIndex)) {
        return prev
      }

      return {
        ...prev,
        completedSteps: [...completedSteps, stepIndex]
      }
    })
  }, [navigationManager])

  /**
   * ✅ UPDATED: Combined function with proper optional step handling
   */
  const handleNavigateWithCompletion = useCallback(async (
    targetIndex: number,
    steps?: EnrollmentStep[]
  ) => {

    if (!steps || steps.length === 0) {
      console.warn('⚠️ No steps provided to handleNavigateWithCompletion')
      return
    }

    if (isNavigating) {
      console.warn('⚠️ Already navigating, skipping request')
      return
    }

    setIsNavigating(true)
    try {
      // Get campaign and step info
      const campaignId = enrollmentDataRef.current.campaignId || enrollmentDataRef.current.selectedCampaign
      const campaign = getCampaignConfig(campaignId)

      if (!campaign) {
        console.error('❌ Campaign not found:', campaignId)

        // Navigate back to campaign selector if callback is provided
        if (onCampaignSelect) {
          onCampaignSelect(null)
        }
        return
      }

      // Validate current step data
      const currentStepId = steps[currentWizardStepIndex]?.id
      const currentStepData = enrollmentDataRef.current[currentStepId] || enrollmentDataRef.current

      const validation = validateStepCompletionInternal(
        currentWizardStepIndex,
        currentStepData,
        campaignId
      )


      // Update step metadata based on validation
      const updatedStepData = {
        ...currentStepData,
        isCompleted: validation.isCompleted,
        isSkipped: validation.isSkipped,
        // Preserve existing submission data
        isFormSubmitted: currentStepData.isFormSubmitted,
        isFormSuccess: currentStepData.isFormSuccess,
        submittedAt: currentStepData.submittedAt
      }

      // Save to enrollmentData
      setEnrollmentData(prev => ({
        ...prev,
        [currentStepId]: updatedStepData,
        // Update completion arrays
        completedSteps: validation.isCompleted
          ? Array.from(new Set([...(prev.completedSteps || []), currentWizardStepIndex]))
          : (prev.completedSteps || []).filter(s => s !== currentWizardStepIndex),
        skippedSteps: validation.isSkipped
          ? Array.from(new Set([...(prev.skippedSteps || []), currentWizardStepIndex]))
          : (prev.skippedSteps || []).filter(s => s !== currentWizardStepIndex)
      }))

      // Wait for state to settle
      await new Promise(resolve => setTimeout(resolve, 50))

      // Navigate to target step
      const savedSession = await navigationManager.handleNavigate(targetIndex, enrollmentDataRef, steps)
      applyMetaFromSession(savedSession)

    } catch (error) {
      console.error('❌ Navigation with completion error:', error)
    } finally {
      setIsNavigating(false)
    }
  }, [currentWizardStepIndex, isNavigating, validateStepCompletionInternal, navigationManager, applyMetaFromSession])

  /**
   * FIXED: Navigate to next step (Continue button)
   */
  const handleContinue = useCallback(async (steps: EnrollmentStep[]) => {
    const nextStep = currentWizardStepIndex + 1

    if (nextStep >= steps.length) {
      console.warn('⚠️ Already on last step')
      return
    }

    await handleNavigateWithCompletion(nextStep, steps)
  }, [currentWizardStepIndex, handleNavigateWithCompletion])

  /**
   * FIXED: Navigate to previous step (Back button)
   */
  const handleBack = useCallback(async (steps: EnrollmentStep[]) => {
    const prevStep = currentWizardStepIndex - 1

    if (prevStep < 0) {
      console.warn('⚠️ Already on first step')
      return
    }

    // Use simple navigate for back button (no completion needed)
    handleSimpleNavigate(prevStep)
  }, [currentWizardStepIndex, handleSimpleNavigate])

  // Lifecycle handlers
  const startNewEnrollment = useCallback(async () => {
    setIsStartingNew(true)
    try {
      await lifecycleManager.startNewEnrollment(currentWizardStepIndex, resetEnrollment, setEnrollmentData)
    } finally {
      setIsStartingNew(false)
    }
  }, [currentWizardStepIndex, lifecycleManager, resetEnrollment, setEnrollmentData])

  const completeEnrollment = useCallback(async () => {
    setIsCompleting(true)
    try {
      await lifecycleManager.completeEnrollment(currentWizardStepIndex, isCompletedMode, resetEnrollment)
    } finally {
      setIsCompleting(false)
    }
  }, [currentWizardStepIndex, isCompletedMode, lifecycleManager, resetEnrollment])

  const startNextChildFromCurrent = useCallback(async () => {
    await lifecycleManager.startNextChildFromCurrent(
      currentWizardStepIndex,
      setEnrollmentData,
      setCurrentWizardStepIndex
    )
  }, [currentWizardStepIndex, lifecycleManager])

  const generateQRCode = useCallback(() => {
    lifecycleManager.generateQRCode()
  }, [lifecycleManager])

  const sendEmailLink = useCallback(async () => {
    await lifecycleManager.sendEmailLink(enrollmentData.clientEmail)
  }, [lifecycleManager, enrollmentData.clientEmail])

  // Data update handlers
  const updateEnrollmentData = useCallback((field: keyof EnrollmentData, value: any) => {

    setEnrollmentData(prev => {
      const next = { ...prev, [field]: value } as EnrollmentData

      // ✅ Special handling for campaign field - also update campaignId
      if (field === 'campaign' && value && value.id) {
        next.campaignId = value.id
      }

      enrollmentDataRef.current = next
      return next
    })
  }, [])

  // Step-specific data update handler
  const updateStepData = useCallback((stepIndex: number, field: string, value: any) => {

    setEnrollmentData(prev => {
      const stepKey = `step${stepIndex + 1}` // Convert 0-based to 1-based
      const currentStepData = prev[stepKey] || {}


      const next = {
        ...prev,
        [stepKey]: {
          ...currentStepData,
          [field]: value
        }
      } as EnrollmentData


      enrollmentDataRef.current = next
      return next
    })
  }, [])

  const updatePlatformData = useCallback((platform: string, field: string, value: any) => {

    setEnrollmentData(prev => {
      // Ensure platforms object exists with default structure
      const currentPlatforms = prev.platforms || {
        xbox: { gamerTagCaptured: false, trophySettingPublic: false, gamerTag: '' },
        playstation: { gamerTagCaptured: false, trophySettingPublic: false, gamerTag: '' },
        steam: { gamerTagCaptured: false, trophySettingPublic: false, gamerTag: '' }
      }

      const next: EnrollmentData = {
        ...prev,
        platforms: {
          ...currentPlatforms,
          [platform]: {
            ...currentPlatforms[platform as keyof typeof currentPlatforms],
            [field]: value,
          },
        },
      }
      enrollmentDataRef.current = next
      return next
    })
  }, [])

  return {
    // State
    sessionId,
    enrollmentData,
    setEnrollmentData,
    currentWizardStepIndex,
    setCurrentWizardStepIndex,
    isCompletedMode,
    setIsCompletedMode,
    agentName,
    isLoading,
    setIsLoading,
    isNavigating,
    isCompleting,
    isStartingNew,

    // Navigation Handlers - FIXED
    handleNavigate,
    handleSimpleNavigate,
    handleNavigateWithCompletion,
    handleContinue,        // NEW: For Continue buttons
    handleBack,            // NEW: For Back buttons
    markStepCompleted,

    // Data Handlers
    updateEnrollmentData,
    updateStepData,
    updatePlatformData,

    // Lifecycle Handlers
    generateQRCode,
    sendEmailLink,
    startNewEnrollment,
    completeEnrollment,
    startNextChildFromCurrent,
    resetEnrollment,

    // Managers (for advanced usage)
    sessionManager,
    navigationManager,
    lifecycleManager,

    // Refs (for effects)
    enrollmentDataRef,
    skipResumeOnceRef,
    movedToPendingRef,
    hiddenTimerRef,
    isRestoringSessionRef,
  }
}